import { useEffect, useRef } from 'react';

import useGeneratikTableStore, { TableScrollView } from '../generatikTableStore';

const ATTACH_FRAME_BUDGET = 120;
const RESTORE_FRAME_BUDGET = 180;
const HEIGHT_SETTLED_FRAMES = 10;
const USER_INPUT_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;

export interface UseTableScrollMemoryParams {
  tableId?: string;
  view: TableScrollView;
  getScroller: () => HTMLElement | null;
  scrollerToken?: unknown;
  ready: boolean;
  skipRestore?: boolean;
}

const useTableScrollMemory = ({
  tableId,
  view,
  getScroller,
  scrollerToken,
  ready,
  skipRestore,
}: UseTableScrollMemoryParams): void => {
  const getScrollerRef = useRef(getScroller);
  getScrollerRef.current = getScroller;

  const scrollTopRef = useRef<number | null>(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    if (!tableId) return undefined;

    let scroller: HTMLElement | null = null;
    let frameId = 0;
    let attempts = 0;

    const handleScroll = () => {
      if (scroller) scrollTopRef.current = scroller.scrollTop;
    };

    const attachWhenScrollerExists = () => {
      scroller = getScrollerRef.current();
      if (!scroller) {
        if (++attempts <= ATTACH_FRAME_BUDGET) {
          frameId = window.requestAnimationFrame(attachWhenScrollerExists);
        }
        return;
      }
      scroller.addEventListener('scroll', handleScroll, { passive: true });
    };
    attachWhenScrollerExists();

    return () => {
      window.cancelAnimationFrame(frameId);
      scroller?.removeEventListener('scroll', handleScroll);
      if (scrollTopRef.current != null) {
        useGeneratikTableStore.getState().setScrollPosition(tableId, view, scrollTopRef.current);
      }
    };
  }, [tableId, view, scrollerToken]);

  useEffect(() => {
    if (!tableId || !ready || skipRestore || restoredRef.current) return undefined;

    const savedTop = useGeneratikTableStore.getState().scrollPositions[tableId]?.[view];
    if (!savedTop) {
      restoredRef.current = true;
      return undefined;
    }

    let frameId = 0;
    let attempts = 0;
    let previousScrollHeight = -1;
    let settledFrames = 0;

    const stopRestoring = () => {
      restoredRef.current = true;
      window.cancelAnimationFrame(frameId);
      USER_INPUT_EVENTS.forEach((event) => window.removeEventListener(event, stopRestoring));
    };

    const applyAndStop = (top: number, scroller: HTMLElement) => {
      scroller.scrollTop = top;
      scrollTopRef.current = top;
      stopRestoring();
    };

    const restoreWhenTallEnough = () => {
      if (++attempts > RESTORE_FRAME_BUDGET) {
        stopRestoring();
        return;
      }
      const scroller = getScrollerRef.current();
      if (!scroller) {
        frameId = window.requestAnimationFrame(restoreWhenTallEnough);
        return;
      }

      const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
      if (maxScrollTop >= savedTop) {
        applyAndStop(savedTop, scroller);
        return;
      }

      settledFrames = scroller.scrollHeight === previousScrollHeight ? settledFrames + 1 : 0;
      previousScrollHeight = scroller.scrollHeight;
      if (settledFrames >= HEIGHT_SETTLED_FRAMES) {
        if (maxScrollTop > 0) applyAndStop(maxScrollTop, scroller);
        else stopRestoring();
        return;
      }
      frameId = window.requestAnimationFrame(restoreWhenTallEnough);
    };

    frameId = window.requestAnimationFrame(restoreWhenTallEnough);
    USER_INPUT_EVENTS.forEach((event) =>
      window.addEventListener(event, stopRestoring, { passive: true }),
    );

    return () => {
      window.cancelAnimationFrame(frameId);
      USER_INPUT_EVENTS.forEach((event) => window.removeEventListener(event, stopRestoring));
    };
  }, [tableId, view, ready, skipRestore]);
};

export default useTableScrollMemory;
